import { useState, useEffect } from "react";
import {
  Shield, Users, Ban, FileText, CreditCard,
  Activity, Database, Key, CheckCircle2, XCircle,
  RefreshCw, Terminal, AlertTriangle, Wrench, Zap, Lock,
  Bot, Settings, BarChart3, ChevronRight, Search,
  UserCheck, UserX, Crown, TrendingUp, AlertCircle,
  Trash2, RotateCcw, ShieldCheck, ToggleLeft, ToggleRight,
  Link as LinkIcon, Hash, Download, Upload, Archive,
  KeyRound, Copy, Eye, EyeOff, ShieldAlert, HardDrive, Tag
} from "lucide-react";
import { AdminPromoSection } from "@/components/AdminPromoSection";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface AdminUser {
  id: string;
  email: string | null;
  display_name: string | null;
  is_banned: boolean;
  searches_today: number;
  created_at: string;
  plan_id: string | null;
  plan_name?: string;
  role?: string;
}

interface AuditLog {
  id: string;
  user_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: any;
  created_at: string;
  ip_address: string | null;
}

interface Payment {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  crypto_currency: string | null;
  created_at: string;
  provider_charge_id: string | null;
  user_email?: string;
}

interface DiagData {
  instance: {
    supabaseUrlHostname: string;
    projectRef: string;
    serviceRoleKeyPresent: boolean;
    coinbaseKeyPresent: boolean;
    timestamp: string;
  };
  database: {
    tables: Record<string, boolean>;
    adminUserCount: number;
  };
  recentAuditEvents: Array<{ action: string; created_at: string; details: any }>;
}

interface AuthDiagData {
  correlationId: string;
  supabaseUrlHostname: string;
  projectRef: string;
  projectRefMatch: boolean;
  expectedProjectRef: string;
  anonKeyLast6: string;
  serviceRoleKeyPresent: boolean;
  dbConnected: boolean;
  timestamp: string;
}

interface Plan {
  id: string;
  name: string;
  display_name: string;
  search_limit_daily: number;
  price_monthly_usd: number;
  export_enabled: boolean;
  collections_enabled: boolean;
  audit_access: boolean;
}

interface BotUser {
  id: string;
  telegram_id: number | null;
  username: string | null;
  is_enabled: boolean;
  linked_user_id: string | null;
  created_at: string;
  api_key_hash: string | null;
  linked_email?: string;
}

interface FeatureFlag {
  id: string;
  name: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

const SUPABASE_PROJECT_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID || "bbygnqkxijbplzuhktfm"}.supabase.co`;

type AdminSection =
  | "overview"
  | "users"
  | "bot-users"
  | "bot-diagnostics"
  | "payments"
  | "coinbase"
  | "plans"
  | "promo-codes"
  | "audit-logs"
  | "security"
  | "diagnostics"
  | "settings"
  | "backup"
  | "bootstrap"
  | "system-audit";

const sidebarItems: { id: AdminSection; label: string; icon: any; adminOnly?: boolean }[] = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "users", label: "Users", icon: Users },
  { id: "bot-users", label: "Bot Users", icon: Bot },
  { id: "bot-diagnostics", label: "Bot Diagnostics", icon: Activity },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "coinbase", label: "Coinbase Commerce", icon: TrendingUp },
  { id: "plans", label: "Plans & Limits", icon: Crown },
  { id: "promo-codes", label: "Promo Codes", icon: Tag },
  { id: "audit-logs", label: "Audit Logs", icon: FileText },
  { id: "security", label: "Security & Abuse", icon: Shield },
  { id: "diagnostics", label: "System Diagnostics", icon: Activity },
  { id: "system-audit", label: "System Audit", icon: ShieldCheck },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "backup", label: "Backup & Export", icon: Archive },
  { id: "bootstrap", label: "Bootstrap", icon: Zap },
];

const BoolBadge = ({ v }: { v: boolean }) =>
  v ? (
    <span className="inline-flex items-center gap-1 text-primary text-xs font-medium">
      <CheckCircle2 className="w-3.5 h-3.5" /> Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-destructive text-xs font-medium">
      <XCircle className="w-3.5 h-3.5" /> No
    </span>
  );

const StatusBadge = ({ banned }: { banned: boolean }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${banned ? "bg-destructive/20 text-destructive" : "bg-primary/20 text-primary"}`}>
    {banned ? "Banned" : "Active"}
  </span>
);

const Admin = () => {
  const { isAdmin, user, profile, roles, session } = useAuth();
  const [section, setSection] = useState<AdminSection>("overview");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Server-side role verification state
  const [serverRoleVerified, setServerRoleVerified] = useState<boolean | null>(null);

  // Data state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [botUsers, setBotUsers] = useState<BotUser[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);

  // Plan editing state
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingPlanValues, setEditingPlanValues] = useState<Partial<Plan>>({});
  const [savingPlanId, setSavingPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [diagData, setDiagData] = useState<DiagData | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [authDiagData, setAuthDiagData] = useState<AuthDiagData | null>(null);
  const [authDiagLoading, setAuthDiagLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [logSearch, setLogSearch] = useState("");
  const [securitySearch, setSecuritySearch] = useState("");

  // User action state
  const [userActionLoading, setUserActionLoading] = useState<string | null>(null);
  const [userActionResult, setUserActionResult] = useState<{ userId: string; result: any } | null>(null);

  // Bootstrap state
  const [bootstrapSecret, setBootstrapSecret] = useState("");
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState<any>(null);
  const [bootstrapFlagEnabled, setBootstrapFlagEnabled] = useState<boolean | null>(null);
  const [repairSecret, setRepairSecret] = useState("");
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState<any>(null);

  // Reset Admin state
  const [resetSecret, setResetSecret] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState<any>(null);

  // Overview stats
  const [stats, setStats] = useState({ totalUsers: 0, bannedUsers: 0, totalPayments: 0, totalRevenue: 0 });

  // Bot diagnostics state
  const [botHealth, setBotHealth] = useState<any>(null);
  const [botHealthLoading, setBotHealthLoading] = useState(false);
  const [botEvents, setBotEvents] = useState<any[]>([]);
  const [webhookSetResult, setWebhookSetResult] = useState<any>(null);
  const [webhookSetLoading, setWebhookSetLoading] = useState(false);

  // System Audit state
  const [auditReport, setAuditReport] = useState<any>(null);
  const [auditRunning, setAuditRunning] = useState(false);

  // Server-side admin role verification on mount — defense-in-depth beyond client-side state
  useEffect(() => {
    const verifyAdminServerSide = async () => {
      if (!user) { setServerRoleVerified(false); return; }
      try {
        const { data, error } = await supabase.rpc("is_admin", { _user_id: user.id });
        setServerRoleVerified(!error && data === true);
      } catch {
        setServerRoleVerified(false);
      }
    };
    verifyAdminServerSide();
  }, [user]);

  useEffect(() => {
    if (serverRoleVerified !== true) return;
    if (section === "overview") fetchOverview();
    if (section === "users") fetchUsers();
    if (section === "audit-logs") fetchLogs();
    if (section === "payments" || section === "coinbase") fetchPayments();
    if (section === "plans") fetchPlans();
    if (section === "diagnostics") fetchDiagnostics();
    if (section === "bootstrap") fetchBootstrapFlag();
    if (section === "bot-users") fetchBotUsers();
    if (section === "security") fetchLogs();
    if (section === "settings") fetchFeatureFlags();
    if (section === "bot-diagnostics") fetchBotDiagnostics();
  }, [section, serverRoleVerified]);

  // Block rendering until server-side verification completes
  if (serverRoleVerified === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (serverRoleVerified === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="text-center">
          <ShieldAlert className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You do not have admin privileges.</p>
        </div>
      </div>
    );
  }

  const fetchOverview = async () => {
    const [{ count: userCount }, { count: bannedCount }, { data: payData }] = await Promise.all([
      supabase.from("profiles").select("id", { count: "exact", head: true }),
      supabase.from("profiles").select("id", { count: "exact", head: true }).eq("is_banned", true),
      supabase.from("payments").select("amount, status"),
    ]);
    const revenue = (payData || []).filter((p) => p.status === "completed").reduce((s, p) => s + Number(p.amount), 0);
    setStats({ totalUsers: userCount || 0, bannedUsers: bannedCount || 0, totalPayments: (payData || []).length, totalRevenue: revenue });
  };

  const fetchUsers = async () => {
    setLoading(true);
    const { data: profileData } = await supabase.from("profiles").select("*").order("created_at", { ascending: false }).limit(200);
    const { data: roleData } = await supabase.from("user_roles").select("user_id, role");
    const { data: planData } = await supabase.from("plans").select("id, name, display_name, search_limit_daily, price_monthly_usd, export_enabled, collections_enabled, audit_access");
    if (profileData) {
      const roleMap = Object.fromEntries((roleData || []).map((r) => [r.user_id, r.role]));
      const planMap = Object.fromEntries((planData || []).map((p: any) => [p.id, p.display_name]));
      setUsers(profileData.map((u: any) => ({ ...u, role: roleMap[u.id] || "free", plan_name: planMap[u.plan_id] || "Free" })));
    }
    if (planData) setPlans(planData as Plan[]);
    setLoading(false);
  };

  const fetchBotUsers = async () => {
    setLoading(true);
    const { data } = await supabase.from("bot_users").select("*").order("created_at", { ascending: false }).limit(200);
    if (data) {
      const linkedIds = data.filter((b: any) => b.linked_user_id).map((b: any) => b.linked_user_id);
      const emailMap: Record<string, string> = {};
      if (linkedIds.length) {
        const { data: pData } = await supabase.from("profiles").select("id, email").in("id", linkedIds);
        (pData || []).forEach((p: any) => { emailMap[p.id] = p.email || "—"; });
      }
      setBotUsers(data.map((b: any) => ({ ...b, linked_email: emailMap[b.linked_user_id] || "—" })));
    }
    setLoading(false);
  };

  const fetchFeatureFlags = async () => {
    setLoading(true);
    const { data } = await supabase.from("feature_flags").select("*").order("name");
    if (data) setFeatureFlags(data as FeatureFlag[]);
    setLoading(false);
  };

  const toggleFeatureFlag = async (flag: FeatureFlag) => {
    const { error } = await supabase.from("feature_flags").update({ enabled: !flag.enabled }).eq("id", flag.id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setFeatureFlags((prev) => prev.map((f) => f.id === flag.id ? { ...f, enabled: !f.enabled } : f));
    toast({ title: `${flag.name} ${!flag.enabled ? "enabled" : "disabled"}` });
  };

  const fetchLogs = async () => {
    setLoading(true);
    const { data } = await supabase.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(500);
    if (data) setLogs(data as AuditLog[]);
    setLoading(false);
  };

  const fetchPayments = async () => {
    setLoading(true);
    const { data } = await supabase.from("payments").select("*").order("created_at", { ascending: false }).limit(200);
    if (data) {
      const emailMap: Record<string, string> = {};
      const uids = [...new Set(data.map((p: any) => p.user_id))];
      if (uids.length) {
        const { data: pData } = await supabase.from("profiles").select("id, email").in("id", uids);
        (pData || []).forEach((p: any) => { emailMap[p.id] = p.email || "—"; });
      }
      setPayments(data.map((p: any) => ({ ...p, user_email: emailMap[p.user_id] || "—" })));
    }
    setLoading(false);
  };

  const fetchBotDiagnostics = async () => {
    setBotHealthLoading(true);
    try {
      const headers: HeadersInit = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {};
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/bot-health`, { headers });
      if (res.ok) setBotHealth(await res.json());
    } catch (e: any) {
      console.error("Bot health fetch error:", e);
    } finally {
      setBotHealthLoading(false);
    }
    const { data } = await supabase.from("bot_events").select("*").order("created_at", { ascending: false }).limit(50);
    if (data) setBotEvents(data);
  };

  const setTelegramWebhook = async (dropPending = false) => {
    if (!session?.access_token) return;
    setWebhookSetLoading(true);
    setWebhookSetResult(null);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/bot-set-webhook`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "set", drop_pending: dropPending }),
      });
      const result = await res.json();
      setWebhookSetResult(result);
      if (result.ok) {
        toast({ title: "✅ Webhook set successfully" });
        await fetchBotDiagnostics();
      } else {
        toast({ title: "Webhook set failed", description: result.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Network error", description: e.message, variant: "destructive" });
    } finally {
      setWebhookSetLoading(false);
    }
  };

  const fetchPlans = async () => {
    setLoading(true);
    const { data } = await supabase.from("plans").select("*").order("price_monthly_usd");
    if (data) setPlans(data as Plan[]);
    setLoading(false);
  };

  const fetchDiagnostics = async () => {
    if (!session?.access_token) return;
    setDiagLoading(true);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/system-diagnostics`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) setDiagData(await res.json());
      else {
        const err = await res.json();
        toast({ title: "Diagnostics error", description: err.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Network error", description: e.message, variant: "destructive" });
    } finally {
      setDiagLoading(false);
    }
  };

  const fetchAuthDiag = async () => {
    if (!session?.access_token) return;
    setAuthDiagLoading(true);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/auth-diag`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) setAuthDiagData(data);
      else toast({ title: "Auth diag error", description: data.error, variant: "destructive" });
    } catch (e: any) {
      toast({ title: "Network error", description: e.message, variant: "destructive" });
    } finally {
      setAuthDiagLoading(false);
    }
  };

  const runAdminUserAction = async (action: string, targetUserId: string, confirm?: string) => {
    if (!session?.access_token) return;
    if (confirm && !window.confirm(confirm)) return;
    setUserActionLoading(`${action}:${targetUserId}`);
    setUserActionResult(null);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-user-actions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action, targetUserId }),
      });
      const result = await res.json();
      setUserActionResult({ userId: targetUserId, result });
      if (result.ok) {
        toast({ title: action === "delete_user" ? "User deleted" : action === "reset_password" ? "Reset link generated" : "Action complete", description: result.message });
        if (action === "delete_user") fetchUsers();
        if (action === "force_verify") fetchUsers();
      } else {
        toast({
          title: "Action failed",
          description: `${result.error} (correlationId: ${result.correlationId})`,
          variant: "destructive",
        });
      }
    } catch (e: any) {
      toast({ title: "Network error", description: e.message, variant: "destructive" });
    } finally {
      setUserActionLoading(null);
    }
  };

  const fetchBootstrapFlag = async () => {
    const { data } = await supabase.from("feature_flags").select("enabled").eq("name", "feature_admin_bootstrap_enabled").single();
    setBootstrapFlagEnabled(data?.enabled ?? false);
  };

  const runBootstrap = async () => {
    if (!bootstrapSecret.trim()) { toast({ title: "Secret required", variant: "destructive" }); return; }
    setBootstrapLoading(true);
    setBootstrapResult(null);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-bootstrap`, {
        method: "POST",
        headers: { "x-bootstrap-secret": bootstrapSecret, "Content-Type": "application/json" },
      });
      const result = await res.json();
      setBootstrapResult(result);
      if (result.ok) { toast({ title: "✅ Bootstrap successful" }); setBootstrapFlagEnabled(false); }
      else toast({ title: "Bootstrap failed", description: result.error, variant: "destructive" });
    } catch (e: any) { toast({ title: "Network error", description: e.message, variant: "destructive" }); }
    finally { setBootstrapLoading(false); setBootstrapSecret(""); }
  };

  const runAdminRepair = async () => {
    if (!repairSecret.trim()) { toast({ title: "Secret required", variant: "destructive" }); return; }
    setRepairLoading(true); setRepairResult(null);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-repair`, {
        method: "POST",
        headers: { "x-repair-secret": repairSecret, "Content-Type": "application/json" },
      });
      const result = await res.json();
      setRepairResult(result);
      if (result.success) toast({ title: "✅ Admin repair successful" });
      else toast({ title: "Repair failed", description: result.error, variant: "destructive" });
    } catch (e: any) { toast({ title: "Network error", description: e.message, variant: "destructive" }); }
    finally { setRepairLoading(false); setRepairSecret(""); }
  };

  const runAdminReset = async () => {
    if (!resetSecret.trim()) { toast({ title: "Secret required", variant: "destructive" }); return; }
    setResetLoading(true); setResetResult(null);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-reset`, {
        method: "POST",
        headers: { "x-reset-secret": resetSecret, "Content-Type": "application/json" },
        body: JSON.stringify({ redirectTo: `${window.location.origin}/set-password` }),
      });
      const result = await res.json();
      setResetResult(result);
      if (result.ok) toast({ title: "✅ Admin reset successful — open the link to set a new password" });
      else toast({ title: "Reset failed", description: result.error, variant: "destructive" });
    } catch (e: any) { toast({ title: "Network error", description: e.message, variant: "destructive" }); }
    finally { setResetLoading(false); }
  };

  const toggleBan = async (userId: string, currentBan: boolean) => {
    const { error } = await supabase.from("profiles").update({ is_banned: !currentBan }).eq("id", userId);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { toast({ title: currentBan ? "User unbanned" : "User banned" }); fetchUsers(); }
  };

  const setUserRole = async (userId: string, newRole: string) => {
    // Delete existing plan-level roles, then insert new one
    const planRoles = ["guest", "free", "pro", "team", "enterprise", "admin"];
    await supabase.from("user_roles").delete().eq("user_id", userId).in("role", planRoles as any);
    const { error } = await supabase.from("user_roles").insert({ user_id: userId, role: newRole as any });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else { toast({ title: "Role updated" }); fetchUsers(); }
  };

  const setUserPlan = async (userId: string, planId: string) => {
    await supabase.from("profiles").update({ plan_id: planId }).eq("id", userId);
    toast({ title: "Plan updated" }); fetchUsers();
  };


  const filteredUsers = users.filter((u) => {
    const q = userSearch.toLowerCase();
    return !q || u.email?.toLowerCase().includes(q) || u.display_name?.toLowerCase().includes(q);
  });

  const filteredLogs = logs.filter((l) => {
    const q = logSearch.toLowerCase();
    return !q || l.action?.toLowerCase().includes(q) || l.target_type?.toLowerCase().includes(q);
  });

  // ---- Overview Stats Panel ----
  const OverviewSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Users", value: stats.totalUsers, icon: Users, color: "text-primary" },
          { label: "Banned Users", value: stats.bannedUsers, icon: Ban, color: "text-destructive" },
          { label: "Payments", value: stats.totalPayments, icon: CreditCard, color: "text-secondary" },
          { label: "Revenue", value: `$${stats.totalRevenue.toFixed(2)}`, icon: TrendingUp, color: "text-primary" },
        ].map((stat) => (
          <div key={stat.label} className="glass-card p-4 border border-border/50">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>
      <div className="glass-card p-5 border border-primary/20">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" /> Admin Identity
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div><div className="text-muted-foreground mb-1">Email</div><div className="font-mono text-foreground truncate">{user?.email}</div></div>
          <div><div className="text-muted-foreground mb-1">Role</div><div className="text-primary font-bold">{roles.join(", ")}</div></div>
          <div><div className="text-muted-foreground mb-1">Plan</div><div className="text-foreground">{profile?.plan_id ? "Enterprise" : "—"}</div></div>
          <div><div className="text-muted-foreground mb-1">Last Login</div><div className="text-foreground">{user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "—"}</div></div>
        </div>
      </div>
    </div>
  );

  // ---- Users Section ----
  const UsersSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users…"
            className="w-full pl-9 pr-4 py-2 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <span className="text-xs text-muted-foreground">{filteredUsers.length} users</span>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">User</th>
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">Role</th>
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">Plan</th>
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">Searches</th>
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">Status</th>
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">Joined</th>
                <th className="text-left p-3 text-muted-foreground font-medium text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isActing = (action: string) => userActionLoading === `${action}:${u.id}`;
                const lastResult = userActionResult?.userId === u.id ? userActionResult.result : null;
                return (
                  <tr key={u.id} className="border-b border-border/20 hover:bg-muted/5 transition-colors">
                    <td className="p-3">
                      <div className="font-medium text-foreground text-xs">{u.display_name || "—"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                    </td>
                    <td className="p-3">
                      <select value={u.role} onChange={(e) => setUserRole(u.id, e.target.value)}
                        className="text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50">
                        {["guest", "free", "pro", "team", "enterprise", "admin"].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3">
                      <select value={u.plan_id || ""} onChange={(e) => setUserPlan(u.id, e.target.value)}
                        className="text-xs bg-muted/30 border border-border/50 rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50">
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-foreground text-xs">{u.searches_today}</td>
                    <td className="p-3"><StatusBadge banned={u.is_banned} /></td>
                    <td className="p-3 text-muted-foreground text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Ban/Unban */}
                        <button onClick={() => toggleBan(u.id, u.is_banned)} title={u.is_banned ? "Unban" : "Ban"}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${u.is_banned ? "bg-primary/10 text-primary hover:bg-primary/20" : "bg-destructive/10 text-destructive hover:bg-destructive/20"}`}>
                          {u.is_banned ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                          {u.is_banned ? "Unban" : "Ban"}
                        </button>
                        {/* Force Verify */}
                        <button onClick={() => runAdminUserAction("force_verify", u.id)}
                          disabled={!!userActionLoading} title="Force verify email"
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50">
                          {isActing("force_verify") ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                          Verify
                        </button>
                        {/* Reset Password */}
                        <button onClick={() => runAdminUserAction("reset_password", u.id)}
                          disabled={!!userActionLoading} title="Generate password reset link"
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50">
                          {isActing("reset_password") ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          Reset
                        </button>
                        {/* Delete User */}
                        <button
                          onClick={() => runAdminUserAction("delete_user", u.id, `Permanently delete ${u.email}? This cannot be undone.`)}
                          disabled={!!userActionLoading} title="Permanently delete user"
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50">
                          {isActing("delete_user") ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Delete
                        </button>
                      </div>
                      {/* Inline result for password reset link */}
                      {lastResult?.ok && lastResult?.resetLink && (
                        <div className="mt-1 p-2 bg-primary/5 border border-primary/20 rounded text-xs">
                          <p className="text-primary font-medium mb-1">Reset link (single-use):</p>
                          <a href={lastResult.resetLink} target="_blank" rel="noopener noreferrer"
                            className="font-mono text-primary underline break-all">{lastResult.resetLink}</a>
                        </div>
                      )}
                      {/* Error with correlation ID */}
                      {lastResult && !lastResult.ok && (
                        <div className="mt-1 p-2 bg-destructive/5 border border-destructive/20 rounded text-xs">
                          <p className="text-destructive">{lastResult.error}</p>
                          {lastResult.correlationId && (
                            <p className="text-muted-foreground mt-0.5 font-mono">ID: {lastResult.correlationId}</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && filteredUsers.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No users found</div>}
        </div>
      </div>
    </div>
  );

  // ---- Payments Section ----
  const PaymentsSection = ({ title }: { title: string }) => (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                {["ID", "User", "Amount", "Status", "Provider", "Crypto", "Date"].map((h) => (
                  <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-border/20 hover:bg-muted/5">
                  <td className="p-3 font-mono text-xs text-muted-foreground">{p.id.slice(0, 8)}…</td>
                  <td className="p-3 text-xs text-foreground">{p.user_email}</td>
                  <td className="p-3 text-xs font-medium text-primary">${Number(p.amount).toFixed(2)} {p.currency}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === "completed" ? "bg-primary/20 text-primary" : p.status === "pending" ? "bg-amber-500/20 text-amber-400" : "bg-destructive/20 text-destructive"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{p.provider}</td>
                  <td className="p-3 text-xs text-muted-foreground">{p.crypto_currency || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && payments.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No payments yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Audit Logs Section ----
  const AuditSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={logSearch} onChange={(e) => setLogSearch(e.target.value)} placeholder="Filter logs…"
            className="w-full pl-9 pr-4 py-2 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <span className="text-xs text-muted-foreground">{filteredLogs.length} entries</span>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                {["Time", "Action", "Target", "IP", "Details"].map((h) => (
                  <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id} className="border-b border-border/20 hover:bg-muted/5">
                  <td className="p-3 text-muted-foreground text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                  <td className="p-3 font-medium text-foreground text-xs">{log.action}</td>
                  <td className="p-3 text-muted-foreground text-xs">{log.target_type ? `${log.target_type}:${log.target_id?.slice(0, 8)}` : "—"}</td>
                  <td className="p-3 text-muted-foreground text-xs font-mono">{log.ip_address || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground max-w-xs truncate font-mono">{JSON.stringify(log.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
        </div>
      </div>
    </div>
  );

  // ---- Diagnostics Section ----
  const DiagnosticsSection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2"><Terminal className="w-4 h-4 text-primary" /> System Diagnostics</h3>
        <button onClick={fetchDiagnostics} disabled={diagLoading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary text-sm hover:bg-primary/20 transition-colors">
          <RefreshCw className={`w-4 h-4 ${diagLoading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {diagLoading && <div className="glass-card p-12 text-center text-muted-foreground">Loading diagnostics…</div>}
      {diagData && (
        <div className="space-y-4">
          <div className="glass-card p-5 border border-border/50">
            <h4 className="text-xs font-bold text-foreground mb-3 flex items-center gap-2"><Database className="w-4 h-4 text-primary" /> Instance</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div><div className="text-muted-foreground mb-1">Host</div><div className="font-mono text-foreground truncate">{diagData.instance.supabaseUrlHostname}</div></div>
              <div><div className="text-muted-foreground mb-1">Project Ref</div><div className="font-mono text-foreground">{diagData.instance.projectRef}</div></div>
              <div><div className="text-muted-foreground mb-1">Service Key</div><BoolBadge v={diagData.instance.serviceRoleKeyPresent} /></div>
              <div><div className="text-muted-foreground mb-1">Coinbase Key</div><BoolBadge v={diagData.instance.coinbaseKeyPresent} /></div>
            </div>
          </div>
          <div className="glass-card p-5 border border-border/50">
            <h4 className="text-xs font-bold text-foreground mb-3 flex items-center gap-2"><Database className="w-4 h-4 text-primary" /> Database Tables <span className="ml-auto text-muted-foreground font-normal">Admin accounts: {diagData.database.adminUserCount}</span></h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(diagData.database.tables).map(([table, ok]) => (
                <div key={table} className={`flex items-center gap-2 p-2 rounded-lg text-xs font-mono ${ok ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                  {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {table}
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-5 border border-primary/20">
            <h4 className="text-xs font-bold text-foreground mb-3 flex items-center gap-2"><Key className="w-4 h-4 text-primary" /> Production Checklist</h4>
            <div className="space-y-2 text-xs">
              {[
                { label: "Correct project ref", ok: diagData.instance.projectRef === "bbygnqkxijbplzuhktfm" },
                { label: "Service role key", ok: diagData.instance.serviceRoleKeyPresent },
                { label: "Admin account exists", ok: diagData.database.adminUserCount > 0 },
                { label: "profiles table", ok: diagData.database.tables["profiles"] },
                { label: "user_roles table", ok: diagData.database.tables["user_roles"] },
                { label: "audit_logs table", ok: diagData.database.tables["audit_logs"] },
              ].map((item, i) => (
                <div key={i} className={`flex items-center gap-2 ${item.ok ? "text-primary" : "text-destructive"}`}>
                  {item.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const savePlan = async (planId: string) => {
    setSavingPlanId(planId);
    const { error } = await supabase.from("plans").update(editingPlanValues as any).eq("id", planId);
    if (error) { toast({ title: "Save failed", description: error.message, variant: "destructive" }); }
    else { toast({ title: "Plan updated ✅" }); setEditingPlanId(null); fetchPlans(); }
    setSavingPlanId(null);
  };

  // ---- Plans Section ----
  const PlansSection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">Plans & Limits</h3>
        <button onClick={fetchPlans} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                {["Name", "Price/mo (USD)", "Daily Limit", "Export", "Collections", "Audit Access", "Actions"].map((h) => (
                  <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(plans as Plan[]).map((p) => {
                const isEditing = editingPlanId === p.id;
                const isSaving = savingPlanId === p.id;
                const vals = isEditing ? editingPlanValues : p;
                return (
                  <tr key={p.id} className={`border-b border-border/20 transition-colors ${isEditing ? "bg-primary/5" : "hover:bg-muted/5"}`}>
                    <td className="p-3">
                      <div className="font-medium text-foreground text-xs">{p.display_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{p.name}</div>
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <input
                          type="number" step="0.01" min="0"
                          value={(vals as any).price_monthly_usd ?? ""}
                          onChange={(e) => setEditingPlanValues((prev) => ({ ...prev, price_monthly_usd: parseFloat(e.target.value) || 0 }))}
                          className="w-24 px-2 py-1 bg-muted/30 border border-primary/40 rounded text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                        />
                      ) : (
                        <span className="text-xs text-primary font-medium">${Number(p.price_monthly_usd).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <input
                          type="number" min="0"
                          value={(vals as any).search_limit_daily ?? ""}
                          onChange={(e) => setEditingPlanValues((prev) => ({ ...prev, search_limit_daily: parseInt(e.target.value) || 0 }))}
                          className="w-28 px-2 py-1 bg-muted/30 border border-primary/40 rounded text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                        />
                      ) : (
                        <span className="text-xs text-foreground">{p.search_limit_daily?.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <input type="checkbox" checked={(vals as any).export_enabled ?? false}
                          onChange={(e) => setEditingPlanValues((prev) => ({ ...prev, export_enabled: e.target.checked }))}
                          className="w-4 h-4 accent-primary" />
                      ) : <BoolBadge v={p.export_enabled} />}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <input type="checkbox" checked={(vals as any).collections_enabled ?? false}
                          onChange={(e) => setEditingPlanValues((prev) => ({ ...prev, collections_enabled: e.target.checked }))}
                          className="w-4 h-4 accent-primary" />
                      ) : <BoolBadge v={p.collections_enabled} />}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <input type="checkbox" checked={(vals as any).audit_access ?? false}
                          onChange={(e) => setEditingPlanValues((prev) => ({ ...prev, audit_access: e.target.checked }))}
                          className="w-4 h-4 accent-primary" />
                      ) : <BoolBadge v={p.audit_access} />}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => savePlan(p.id)} disabled={isSaving}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-primary/20 text-primary border border-primary/30 hover:bg-primary/30 transition-colors disabled:opacity-50">
                            {isSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Save
                          </button>
                          <button onClick={() => setEditingPlanId(null)}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                            <XCircle className="w-3 h-3" /> Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingPlanId(p.id); setEditingPlanValues({ price_monthly_usd: p.price_monthly_usd, search_limit_daily: p.search_limit_daily, export_enabled: p.export_enabled, collections_enabled: p.collections_enabled, audit_access: p.audit_access }); }}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
        </div>
      </div>
    </div>
  );

  // ---- Bootstrap Section ----
  const BootstrapSection = () => (
    <div className="space-y-4">
      <div className="glass-card p-5 border border-primary/20">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-primary" /> Admin Bootstrap</h3>
        <p className="text-xs text-muted-foreground mb-4">One-time endpoint to bootstrap the admin account.</p>
        <div className={`flex items-center gap-2 text-xs font-medium mb-4 px-3 py-2 rounded-lg border ${bootstrapFlagEnabled === null ? "bg-muted/20 border-border/40 text-muted-foreground" : bootstrapFlagEnabled ? "bg-primary/10 border-primary/30 text-primary" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
          {bootstrapFlagEnabled === null ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : bootstrapFlagEnabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Bootstrap endpoint is ENABLED</> : <><Lock className="w-3.5 h-3.5" /> Bootstrap endpoint is DISABLED</>}
        </div>
        {bootstrapFlagEnabled && (
          <div className="space-y-3">
            <input type="password" autoComplete="off" value={bootstrapSecret} onChange={(e) => setBootstrapSecret(e.target.value)} placeholder="ADMIN_BOOTSTRAP_SECRET"
              className="w-full px-3 py-2 bg-muted/30 border border-border/50 rounded-lg text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <button onClick={runBootstrap} disabled={bootstrapLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary/20 border border-primary/40 text-primary text-sm font-semibold hover:bg-primary/30 transition-colors disabled:opacity-50">
              <Zap className="w-4 h-4" /> {bootstrapLoading ? "Running…" : "Run Bootstrap"}
            </button>
          </div>
        )}
        {bootstrapResult && (
          <div className={`mt-4 p-4 rounded-lg border text-sm ${bootstrapResult.ok ? "border-primary/30 bg-primary/5" : "border-destructive/30 bg-destructive/5"}`}>
            {bootstrapResult.ok ? (
              <>
                <p className="font-bold text-primary mb-2">✅ Bootstrap Successful</p>
                {bootstrapResult.one_time_set_password_url && (
                  <div className="p-3 bg-muted/20 border border-border/50 rounded-lg">
                    <p className="text-xs font-semibold text-foreground mb-2">🔑 One-Time Password Setup Link:</p>
                    <a href={bootstrapResult.one_time_set_password_url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-primary underline break-all">
                      {bootstrapResult.one_time_set_password_url}
                    </a>
                  </div>
                )}
              </>
            ) : <p className="font-bold text-destructive">❌ {bootstrapResult.error}</p>}
          </div>
        )}
      </div>
      <div className="glass-card p-5 border border-border/50">
        <h4 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" /> Admin Repair</h4>
        <p className="text-xs text-muted-foreground mb-3">Recover admin access if identity is inconsistent. Enter your repair secret below.</p>
        <div className="space-y-3">
          <input
            type="password"
            autoComplete="off"
            value={repairSecret}
            onChange={(e) => setRepairSecret(e.target.value)}
            placeholder="ADMIN_REPAIR_SECRET"
            className="w-full px-3 py-2 bg-muted/30 border border-border/50 rounded-lg text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <button onClick={runAdminRepair} disabled={repairLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm font-medium hover:bg-destructive/20 transition-colors disabled:opacity-50">
            <Wrench className="w-4 h-4" /> {repairLoading ? "Running…" : "Run Admin Repair"}
          </button>
        </div>
        {repairResult && (
          <div className={`mt-3 p-3 rounded-lg border text-xs ${repairResult.success ? "border-primary/30 bg-primary/5 text-primary" : "border-destructive/30 bg-destructive/5 text-destructive"}`}>
            {repairResult.success ? `✅ ${repairResult.steps?.join(", ")}` : `❌ ${repairResult.error}`}
          </div>
        )}
      </div>

      {/* ── Reset Admin (Full Targeted Reset) ── */}
      <div className="glass-card p-5 border border-destructive/30">
        <h4 className="text-sm font-bold text-foreground mb-1 flex items-center gap-2">
          <RotateCcw className="w-4 h-4 text-destructive" /> Reset Admin Account
        </h4>
        <p className="text-xs text-muted-foreground mb-1">
          Deletes and recreates <span className="font-mono text-foreground">jp-empire@eay.jp</span> only. All other users are untouched.
          Returns a one-time set-password URL.
        </p>
        <div className="flex items-center gap-1.5 text-xs text-secondary bg-secondary/10 border border-secondary/20 rounded-lg px-3 py-1.5 mb-3">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Irreversible — existing admin session will be invalidated.
        </div>
        <div className="space-y-3">
          <input
            type="password"
            value={resetSecret}
            onChange={(e) => setResetSecret(e.target.value)}
            placeholder="ADMIN_REPAIR_SECRET"
            className="w-full px-3 py-2 bg-muted/30 border border-border/50 rounded-lg text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive/50"
          />
          <button onClick={runAdminReset} disabled={resetLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive/20 border border-destructive/40 text-destructive text-sm font-semibold hover:bg-destructive/30 transition-colors disabled:opacity-50">
            <RotateCcw className="w-4 h-4" /> {resetLoading ? "Resetting…" : "Reset Admin (jp-empire@eay.jp)"}
          </button>
        </div>
        {resetResult && (
          <div className={`mt-4 p-4 rounded-lg border text-sm ${resetResult.ok ? "border-primary/30 bg-primary/5" : "border-destructive/30 bg-destructive/5"}`}>
            {resetResult.ok ? (
              <div className="space-y-3">
                <p className="font-bold text-primary">✅ Admin Reset Successful</p>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {resetResult.steps?.map((s: string, i: number) => (
                    <div key={i} className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-primary shrink-0" /> {s}</div>
                  ))}
                </div>
                {resetResult.set_password_url && (
                  <div className="p-3 bg-muted/20 border border-border/50 rounded-lg">
                    <p className="text-xs font-semibold text-foreground mb-2">🔑 One-Time Set-Password Link:</p>
                    <p className="text-xs font-mono text-muted-foreground break-all mb-2">{resetResult.set_password_url}</p>
                    <a
                      href={resetResult.set_password_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                    >
                      <LinkIcon className="w-3.5 h-3.5" /> Open Set-Password Page
                    </a>
                  </div>
                )}
              </div>
            ) : <p className="font-bold text-destructive">❌ {resetResult.error}</p>}
          </div>
        )}
      </div>
    </div>
  );

  // ---- Bot Users Section ----
  const BotUsersSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2"><Bot className="w-4 h-4 text-primary" /> Telegram Bot Users</h3>
        <button onClick={fetchBotUsers} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                {["Telegram ID", "Username", "Linked User", "API Key", "Status", "Created"].map((h) => (
                  <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {botUsers.map((b) => (
                <tr key={b.id} className="border-b border-border/20 hover:bg-muted/5">
                  <td className="p-3 font-mono text-xs text-foreground">{b.telegram_id || "—"}</td>
                  <td className="p-3 text-xs text-foreground">{b.username ? `@${b.username}` : "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{b.linked_email || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground font-mono">{b.api_key_hash ? `${b.api_key_hash.slice(0, 8)}…` : "—"}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.is_enabled ? "bg-primary/20 text-primary" : "bg-muted/30 text-muted-foreground"}`}>
                      {b.is_enabled ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && botUsers.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              <Bot className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No bot users registered yet</p>
              <p className="text-xs mt-1">Telegram bot users will appear here when they connect</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Security & Abuse Section ----
  const SECURITY_EVENTS = ["login_failed", "unauthorized", "ban", "rate_limit", "abuse", "delete", "repair", "bootstrap", "forbidden"];
  const securityLogs = logs.filter((l) => {
    const isSecurityEvent = SECURITY_EVENTS.some((e) => l.action?.toLowerCase().includes(e));
    const q = securitySearch.toLowerCase();
    if (!isSecurityEvent && !q) return false;
    if (q) return l.action?.toLowerCase().includes(q) || l.ip_address?.includes(q);
    return isSecurityEvent;
  });

  const SecuritySection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={securitySearch} onChange={(e) => setSecuritySearch(e.target.value)} placeholder="Filter security events…"
            className="w-full pl-9 pr-4 py-2 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
        </div>
        <span className="text-xs text-muted-foreground">{securityLogs.length} events</span>
        <button onClick={fetchLogs} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-2">
        {[
          { label: "Failed Logins", count: logs.filter(l => l.action?.includes("login_failed") || l.action?.includes("invalid_credentials")).length, color: "text-destructive" },
          { label: "Unauthorized Attempts", count: logs.filter(l => l.action?.includes("unauthorized")).length, color: "text-amber-400" },
          { label: "Admin Actions", count: logs.filter(l => l.action?.startsWith("admin_")).length, color: "text-primary" },
          { label: "Ban Events", count: logs.filter(l => l.action?.includes("ban")).length, color: "text-secondary" },
        ].map((s) => (
          <div key={s.label} className="glass-card p-3 border border-border/30">
            <div className={`text-xl font-bold ${s.color}`}>{s.count}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                {["Time", "Event", "Target", "IP Address", "Details"].map((h) => (
                  <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(securitySearch ? securityLogs : logs.filter(l => SECURITY_EVENTS.some(e => l.action?.toLowerCase().includes(e)))).slice(0, 200).map((log) => (
                <tr key={log.id} className="border-b border-border/20 hover:bg-muted/5">
                  <td className="p-3 text-muted-foreground text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs">
                    <span className={`font-medium ${log.action?.includes("delete") || log.action?.includes("ban") || log.action?.includes("unauthorized") ? "text-destructive" : log.action?.startsWith("admin_") ? "text-primary" : "text-foreground"}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground text-xs">{log.target_type ? `${log.target_type}:${log.target_id?.slice(0, 8)}` : "—"}</td>
                  <td className="p-3 text-muted-foreground text-xs font-mono">{log.ip_address || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground max-w-xs truncate font-mono">{JSON.stringify(log.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && securityLogs.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No security events found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Settings / Feature Flags Section ----
  const SettingsSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2"><ToggleRight className="w-4 h-4 text-primary" /> Feature Flags</h3>
        <button onClick={fetchFeatureFlags} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/10">
                {["Flag", "Description", "Status", "Toggle"].map((h) => (
                  <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureFlags.map((flag) => (
                <tr key={flag.id} className="border-b border-border/20 hover:bg-muted/5">
                  <td className="p-3 font-mono text-xs text-foreground">{flag.name}</td>
                  <td className="p-3 text-xs text-muted-foreground max-w-xs">{flag.description || "—"}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${flag.enabled ? "bg-primary/20 text-primary" : "bg-muted/30 text-muted-foreground"}`}>
                      {flag.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="p-3">
                    <button onClick={() => toggleFeatureFlag(flag)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${flag.enabled ? "bg-destructive/10 text-destructive hover:bg-destructive/20" : "bg-primary/10 text-primary hover:bg-primary/20"}`}>
                      {flag.enabled ? <><ToggleLeft className="w-3 h-3" /> Disable</> : <><ToggleRight className="w-3 h-3" /> Enable</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && featureFlags.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              <Settings className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No feature flags configured</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Bot Diagnostics Section ----
  const BotDiagnosticsSection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <Bot className="w-4 h-4 text-primary" /> Bot Diagnostics
        </h3>
        <div className="flex gap-2">
          <button onClick={fetchBotDiagnostics} disabled={botHealthLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${botHealthLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={() => setTelegramWebhook(true)} disabled={webhookSetLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-secondary/10 border border-secondary/30 text-secondary hover:bg-secondary/20 transition-colors disabled:opacity-50">
            <Wrench className={`w-3 h-3 ${webhookSetLoading ? "animate-spin" : ""}`} /> Repair Webhook
          </button>
        </div>
      </div>

      {/* Webhook secret missing warning */}
      {botHealth && botHealth.webhook_secret_present === false && (
        <div className="rounded-lg border-2 border-destructive bg-destructive/10 p-4">
          <div className="flex items-center gap-2 text-destructive font-bold text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            ⚠ Webhook secret not configured — Bot accepts unauthenticated requests
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Set <code className="font-mono text-destructive">TELEGRAM_WEBHOOK_SECRET_TOKEN</code> in Cloud secrets to secure the webhook endpoint.
          </p>
        </div>
      )}

      {/* Auto-registered indicator */}
      {botHealth?.auto_registered && (
        <div className={`rounded-lg border p-3 text-xs ${botHealth.auto_register_error ? "border-destructive/30 bg-destructive/10" : "border-primary/30 bg-primary/10"}`}>
          <div className="flex items-center gap-2 font-semibold">
            <Zap className="w-3.5 h-3.5" />
            {botHealth.auto_register_error
              ? `⚠ Auto-register attempted but failed: ${botHealth.auto_register_error}`
              : "✅ Webhook auto-registered successfully on this health check"}
          </div>
        </div>
      )}

      {/* Webhook mismatch banner */}
      {botHealth?.webhook_status && (() => {
        const expectedUrl = botHealth.expected_webhook_url || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/telegram-webhook`;
        const currentUrl = botHealth.webhook_status.url || "";
        const isMasked = currentUrl.includes("[MASKED]");
        const expectedHost = (() => { try { return new URL(expectedUrl).hostname; } catch { return ""; } })();
        const currentHost = currentUrl ? (() => { try { return new URL(currentUrl.replace("bot[MASKED]", "botXXX")).hostname; } catch { return ""; } })() : "";
        const mismatch = currentUrl && currentUrl !== "(none)" && currentHost !== expectedHost;
        if (!mismatch) return null;
        return (
          <div className="rounded-lg border-2 border-destructive bg-destructive/10 p-4 space-y-2">
            <div className="flex items-center gap-2 text-destructive font-bold text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              WEBHOOK MISMATCH — Bot is pointing to wrong project
            </div>
            <div className="text-xs font-mono text-muted-foreground space-y-1">
              <div><span className="text-destructive font-semibold">Current:</span> {currentUrl}</div>
              <div><span className="text-primary font-semibold">Expected:</span> {expectedUrl}</div>
            </div>
            <button
              onClick={() => setTelegramWebhook(true)}
              disabled={webhookSetLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-xs rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 font-bold transition-colors disabled:opacity-50"
            >
              <Wrench className={`w-3.5 h-3.5 ${webhookSetLoading ? "animate-spin" : ""}`} />
              {webhookSetLoading ? "Fixing…" : "🔧 Fix Webhook Now (drops pending updates)"}
            </button>
          </div>
        );
      })()}

      {/* Health card */}
      {botHealth && (
        <div className="glass-card p-5 border border-border/50 space-y-4">
          <div className="flex items-center gap-3">
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${botHealth.ok ? "bg-primary/20 text-primary border-primary/30" : "bg-destructive/20 text-destructive border-destructive/30"}`}>
              {botHealth.ok ? "🟢 ONLINE" : "🔴 OFFLINE"}
            </span>
            <span className="text-xs text-muted-foreground font-mono">v{botHealth.version}</span>
            <span className="text-xs text-muted-foreground ml-auto">{botHealth.timestamp ? new Date(botHealth.timestamp).toLocaleString() : ""}</span>
          </div>
          {botHealth.bot && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div><div className="text-muted-foreground mb-1">Bot Username</div><div className="font-mono text-foreground">@{botHealth.bot.username ?? "—"}</div></div>
              <div><div className="text-muted-foreground mb-1">Bot Name</div><div className="text-foreground">{botHealth.bot.name ?? "—"}</div></div>
              <div><div className="text-muted-foreground mb-1">Bot ID</div><div className="font-mono text-foreground">{botHealth.bot.id ?? "—"}</div></div>
            </div>
          )}
          {botHealth.webhook_status && (
            <div className="bg-muted/20 rounded-lg p-3 border border-border/30 text-xs space-y-1.5">
              <div className="font-semibold text-foreground mb-2 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-primary" /> Webhook Status</div>
              <div><span className="text-muted-foreground">URL: </span><span className="font-mono text-foreground break-all">{botHealth.webhook_status.url || "(not set)"}</span></div>
              <div><span className="text-muted-foreground">Pending updates: </span><span className={`font-medium ${(botHealth.webhook_status.pending_update_count ?? 0) > 0 ? "text-destructive" : "text-primary"}`}>{botHealth.webhook_status.pending_update_count ?? 0}</span></div>
              {botHealth.webhook_status.last_error_message && (
                <div className="font-medium text-destructive">
                  ⚠ Last error: {botHealth.webhook_status.last_error_message}
                </div>
              )}
              {botHealth.webhook_status.last_error_date && (
                <div className="text-muted-foreground">Error at: {new Date(botHealth.webhook_status.last_error_date * 1000).toLocaleString()}</div>
              )}
            </div>
          )}

          {/* Last set webhook + secret presence */}
          <div className="bg-muted/20 rounded-lg p-3 border border-border/30 text-xs space-y-1.5">
            <div className="font-semibold text-foreground mb-2 flex items-center gap-1.5"><Settings className="w-3.5 h-3.5 text-primary" /> Webhook Registration</div>
            <div><span className="text-muted-foreground">Last set at: </span><span className="font-mono text-foreground">{botHealth.last_set_webhook_at ? new Date(botHealth.last_set_webhook_at).toLocaleString() : "Never"}</span></div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Webhook secret: </span>
              {botHealth.webhook_secret_present
                ? <span className="text-primary font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Configured</span>
                : <span className="text-destructive font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Not set</span>}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(botHealth.env_presence ?? {}).map(([k, v]) => (
              <div key={k} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs font-mono ${v ? "bg-primary/10 border-primary/20 text-primary" : "bg-destructive/10 border-destructive/20 text-destructive"}`}>
                {v ? <CheckCircle2 className="w-3 h-3 shrink-0" /> : <XCircle className="w-3 h-3 shrink-0" />}
                <span className="truncate">{k.replace(/_/g, "_\u200B")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Webhook set result */}
      {webhookSetResult && (
        <div className={`glass-card p-4 border text-xs ${webhookSetResult.ok ? "border-primary/30" : "border-destructive/30"}`}>
          {webhookSetResult.ok
            ? <><p className="text-primary font-semibold mb-1">✅ Webhook registered</p><p className="font-mono text-muted-foreground break-all">{webhookSetResult.webhookUrl}</p></>
            : <p className="text-destructive">❌ {webhookSetResult.error}</p>
          }
        </div>
      )}

      {/* Recent bot events */}
      <div className="glass-card overflow-hidden">
        <div className="p-3 border-b border-border/30 text-xs font-semibold text-foreground flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5 text-primary" /> Last 50 Bot Events
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/30 bg-muted/10">
                {["Time", "Type", "Command", "Details"].map(h => (
                  <th key={h} className="text-left p-2.5 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {botEvents.map((e) => (
                <tr key={e.id} className="border-b border-border/10 hover:bg-muted/5">
                  <td className="p-2.5 text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="p-2.5 font-medium text-foreground">{e.event_type}</td>
                  <td className="p-2.5 font-mono text-primary">{e.command ?? "—"}</td>
                  <td className="p-2.5 text-muted-foreground max-w-xs truncate font-mono">{JSON.stringify(e.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {botEvents.length === 0 && !botHealthLoading && (
            <div className="p-8 text-center text-muted-foreground text-xs">No bot events yet. Events appear after users interact with the bot.</div>
          )}
        </div>
      </div>
    </div>
  );

  // ---- Backup & Export Section ----
  const BackupSection = () => {
    const [exportPassword, setExportPassword] = useState("");
    const [backupList, setBackupList] = useState<any[]>([]);
    const [backupLoading, setBackupLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [storeLoading, setStoreLoading] = useState(false);
    const [restoreLoading, setRestoreLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [storeResult, setStoreResult] = useState<any>(null);
    const [restoreResult, setRestoreResult] = useState<any>(null);
    // Decrypt panel
    const [decryptFile, setDecryptFile] = useState<File | null>(null);
    const [decryptPassword, setDecryptPassword] = useState("");
    const [decryptLoading, setDecryptLoading] = useState(false);

    useEffect(() => {
      fetchBackupList();
    }, []);

    const fetchBackupList = async () => {
      if (!session?.access_token) return;
      setListLoading(true);
      try {
        const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-export?action=list`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBackupList(data.backups || []);
        }
      } catch {} finally { setListLoading(false); }
    };

    const triggerExport = async (storeMode: boolean) => {
      if (!session?.access_token) return;
      if (!exportPassword.trim()) {
        toast({ title: "Password required", description: "Enter an export password.", variant: "destructive" });
        return;
      }

      storeMode ? setStoreLoading(true) : setExportLoading(true);
      try {
        const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-export`, {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            mode: storeMode ? "store" : "download",
            encryption: "password",
            password: exportPassword,
          }),
        });

        if (storeMode) {
          const data = await res.json();
          if (data.ok) {
            setStoreResult(data);
            toast({ title: "✅ Backup stored", description: `${(data.size / 1024).toFixed(1)} KB encrypted` });
            fetchBackupList();
          } else {
            toast({ title: "Store failed", description: data.error, variant: "destructive" });
          }
        } else {
          if (!res.ok) {
            const err = await res.json();
            toast({ title: "Export failed", description: err.error, variant: "destructive" });
            return;
          }
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          const dateStr = new Date().toISOString().slice(0, 10);
          a.href = url;
          a.download = `jp-ai-dorks-backup-${dateStr}.zip.enc`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast({ title: "✅ Backup downloaded", description: `${(blob.size / 1024).toFixed(1)} KB encrypted archive` });
        }
      } catch (e: any) {
        toast({ title: "Network error", description: e.message, variant: "destructive" });
      } finally {
        storeMode ? setStoreLoading(false) : setExportLoading(false);
      }
    };

    const triggerRestore = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !session?.access_token) return;
      if (!window.confirm(`Restore from "${file.name}"? This will OVERWRITE live data. Make sure you have decrypted the archive first.`)) return;
      setRestoreLoading(true);
      setRestoreResult(null);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-export`, {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: formData,
        });
        const data = await res.json();
        setRestoreResult(data);
        if (data.ok) toast({ title: "✅ Restore complete", description: `${data.restoredTables?.length} tables restored` });
        else toast({ title: "Restore failed", description: data.error, variant: "destructive" });
      } catch (e: any) {
        toast({ title: "Network error", description: e.message, variant: "destructive" });
      } finally { setRestoreLoading(false); e.target.value = ""; }
    };

    const decryptArchive = async () => {
      if (!decryptFile) { toast({ title: "Select a .zip.enc file", variant: "destructive" }); return; }
      setDecryptLoading(true);
      try {
        const encBytes = new Uint8Array(await decryptFile.arrayBuffer());
        let decrypted: ArrayBuffer;

        if (!decryptPassword.trim()) { toast({ title: "Enter your password", variant: "destructive" }); setDecryptLoading(false); return; }
        const salt = encBytes.slice(0, 16);
        const iv = encBytes.slice(16, 28);
        const ciphertext = encBytes.slice(28);
        const enc2 = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey("raw", enc2.encode(decryptPassword), "PBKDF2", false, ["deriveKey"]);
        const cryptoKey = await crypto.subtle.deriveKey(
          { name: "PBKDF2", salt, iterations: 310000, hash: "SHA-256" },
          keyMaterial,
          { name: "AES-GCM", length: 256 }, false, ["decrypt"]
        );
        decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, cryptoKey, ciphertext);

        const blob = new Blob([decrypted], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = decryptFile.name.replace(".enc", "");
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({ title: "✅ Decrypted successfully", description: "ZIP file downloaded." });
      } catch (e: any) {
        toast({ title: "Decryption failed", description: "Wrong key/password or corrupted file.", variant: "destructive" });
      } finally { setDecryptLoading(false); }
    };

    return (
      <div className="space-y-6">
        {/* Panel 1: Export */}
        <div className="glass-card border border-border/50 overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-border/30 bg-muted/10">
            <Download className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Export Full Backup</h3>
            <span className="ml-auto text-xs text-muted-foreground">AES-256-GCM · Password Encryption</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
              {["profiles", "user_roles", "plans", "subscriptions", "payments", "queries", "results", "collections", "categories", "dork_templates", "feature_flags", "audit_logs", "bot_users (api_key_hash stripped)"].map((t) => (
                <div key={t} className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-primary shrink-0" />{t}</div>
              ))}
            </div>
            <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20 text-xs text-muted-foreground flex items-start gap-2">
              <ShieldAlert className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
              <span>Never included: API keys, service role keys, webhook secrets, passwords.</span>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Export Password</label>
              <input type="password" value={exportPassword} onChange={(e) => setExportPassword(e.target.value)}
                placeholder="Enter a strong password for this export…"
                className="w-full px-3 py-2 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <p className="text-xs text-muted-foreground mt-1">Password is used only for this export and never stored. You will need it to decrypt.</p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => triggerExport(false)} disabled={exportLoading || storeLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
                {exportLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download Encrypted Backup
              </button>
              <button onClick={() => triggerExport(true)} disabled={exportLoading || storeLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary/10 border border-secondary/30 text-secondary text-sm font-medium hover:bg-secondary/20 transition-colors disabled:opacity-50">
                {storeLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />}
                Store in Cloud
              </button>
            </div>

            {storeResult && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs space-y-1">
                <p className="text-primary font-semibold">✅ Stored: {storeResult.filename}</p>
                <p className="text-muted-foreground">Size: {(storeResult.size / 1024).toFixed(1)} KB</p>
                {storeResult.signedUrl && (
                  <a href={storeResult.signedUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">Download (expires in 1h)</a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Panel 3: Backup History */}
        <div className="glass-card border border-border/50 overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-border/30 bg-muted/10">
            <Archive className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Backup History</h3>
            <button onClick={fetchBackupList} disabled={listLoading} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors">
              <RefreshCw className={`w-3 h-3 ${listLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/30 bg-muted/10">
                  {["Filename", "Size", "Created", "Download"].map((h) => (
                    <th key={h} className="text-left p-3 text-muted-foreground font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backupList.map((b) => (
                  <tr key={b.id} className="border-b border-border/20 hover:bg-muted/5">
                    <td className="p-3 text-xs font-mono text-foreground">{b.name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{b.size ? `${(b.size / 1024).toFixed(1)} KB` : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</td>
                    <td className="p-3">
                      {b.signedUrl ? (
                        <a href={b.signedUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors w-fit">
                          <Download className="w-3 h-3" /> Download
                        </a>
                      ) : <span className="text-xs text-muted-foreground">Expired</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!listLoading && backupList.length === 0 && (
              <div className="p-10 text-center text-muted-foreground">
                <Archive className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No stored backups yet</p>
                <p className="text-xs mt-1">Use "Store in Cloud" to save encrypted archives here</p>
              </div>
            )}
          </div>
        </div>

        {/* Panel 4: Restore */}
        <div className="glass-card border border-destructive/20 overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-destructive/20 bg-destructive/5">
            <Upload className="w-4 h-4 text-destructive" />
            <h3 className="text-sm font-semibold text-foreground">Restore from Backup</h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-semibold text-destructive">Restore overwrites live data. This cannot be undone.</p>
                <p>Decrypt the <code className="font-mono">.zip.enc</code> archive locally using the Decrypt panel below, then upload the decrypted <code className="font-mono">.zip</code> here.</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">Upload Decrypted ZIP Archive</label>
              <div className="flex items-center gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${restoreLoading ? "opacity-50 cursor-not-allowed bg-muted/20 border-border/30 text-muted-foreground" : "bg-destructive/10 border-destructive/30 text-destructive hover:bg-destructive/20"}`}>
                  {restoreLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {restoreLoading ? "Restoring…" : "Choose Decrypted ZIP"}
                  <input type="file" accept=".zip" className="hidden" onChange={triggerRestore} disabled={restoreLoading} />
                </label>
              </div>
            </div>
            {restoreResult && (
              <div className={`p-3 rounded-lg text-xs border ${restoreResult.ok ? "bg-primary/5 border-primary/20" : "bg-destructive/5 border-destructive/20"}`}>
                {restoreResult.ok ? (
                  <>
                    <p className="text-primary font-semibold mb-1">✅ Restore complete</p>
                    <p className="text-muted-foreground">Tables restored: {restoreResult.restoredTables?.join(", ")}</p>
                    <p className="text-muted-foreground mt-0.5">Row counts: {JSON.stringify(restoreResult.rowCounts)}</p>
                  </>
                ) : (
                  <p className="text-destructive">{restoreResult.error}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Panel 4: Client-side Decrypt */}
        <div className="glass-card border border-border/50 overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-border/30 bg-muted/10">
            <Lock className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Decrypt Archive (Browser, Local)</h3>
            <span className="ml-auto text-xs text-muted-foreground">No data sent to server</span>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-xs text-muted-foreground">Upload a <code className="font-mono">.zip.enc</code> file and enter the password you used during export. Decryption happens entirely in your browser.</p>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Encrypted File (.zip.enc)</label>
              <input type="file" accept=".enc,.zip.enc" onChange={(e) => setDecryptFile(e.target.files?.[0] || null)}
                className="text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-border/50 file:bg-muted/30 file:text-xs file:font-medium file:text-foreground hover:file:bg-muted/50 file:transition-colors" />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Password</label>
              <input type="password" value={decryptPassword} onChange={(e) => setDecryptPassword(e.target.value)}
                placeholder="Enter the export password…"
                className="w-full px-3 py-2 bg-muted/30 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>

            <button onClick={decryptArchive} disabled={decryptLoading || !decryptFile}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/20 transition-colors disabled:opacity-50">
              {decryptLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Decrypt & Download ZIP
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---- System Audit Section ----

  const runSystemAudit = async () => {
    if (!session?.access_token) return;
    setAuditRunning(true);
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-selftest`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.ok) { setAuditReport(data.report); toast({ title: `Audit: ${data.report.summary.passed}/${data.report.summary.total} passed` }); }
      else toast({ title: "Audit failed", description: data.error, variant: "destructive" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
    finally { setAuditRunning(false); }
  };

  const loadLatestAudit = async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/admin-selftest`, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.ok && data.report?.report_json) setAuditReport(data.report.report_json);
    } catch {}
  };

  const exportAuditJSON = () => {
    if (!auditReport) return;
    const blob = new Blob([JSON.stringify(auditReport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `system-audit-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SystemAuditSection = () => (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <button onClick={runSystemAudit} disabled={auditRunning}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
          <ShieldCheck className={`w-4 h-4 ${auditRunning ? "animate-spin" : ""}`} /> Run Full Audit
        </button>
        <button onClick={loadLatestAudit}
          className="flex items-center gap-2 px-4 py-2 bg-muted/30 text-muted-foreground rounded-lg text-sm hover:bg-muted/50">
          <RefreshCw className="w-4 h-4" /> View Latest Report
        </button>
        {auditReport && (
          <button onClick={exportAuditJSON}
            className="flex items-center gap-2 px-4 py-2 bg-muted/30 text-muted-foreground rounded-lg text-sm hover:bg-muted/50">
            <Download className="w-4 h-4" /> Export JSON
          </button>
        )}
      </div>
      {auditReport && (
        <div className="glass-card border border-border/50 overflow-hidden">
          <div className="p-4 border-b border-border/30 flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{auditReport.summary.passed}/{auditReport.summary.total} passed</span>
            {auditReport.summary.failed > 0 && (
              <span className="text-xs bg-destructive/20 text-destructive px-2 py-0.5 rounded-full">{auditReport.summary.failed} failed</span>
            )}
            <span className="text-xs text-muted-foreground ml-auto font-mono">{auditReport.timestamp}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/30 bg-muted/10">
                  <th className="text-left p-3 text-muted-foreground text-xs font-medium">Test</th>
                  <th className="text-left p-3 text-muted-foreground text-xs font-medium">Status</th>
                  <th className="text-left p-3 text-muted-foreground text-xs font-medium">Detail</th>
                  <th className="text-right p-3 text-muted-foreground text-xs font-medium">Duration</th>
                </tr>
              </thead>
              <tbody>
                {auditReport.tests.map((t: any, i: number) => (
                  <tr key={i} className="border-b border-border/20">
                    <td className="p-3 font-mono text-xs text-foreground">{t.name}</td>
                    <td className="p-3">
                      {t.status === "PASS" ? (
                        <span className="inline-flex items-center gap-1 text-primary text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> PASS</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-destructive text-xs"><XCircle className="w-3.5 h-3.5" /> FAIL</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground max-w-xs truncate">{t.detail}</td>
                    <td className="p-3 text-right text-xs text-muted-foreground">{t.durationMs}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const sectionContent: Record<AdminSection, React.ReactNode> = {
    overview: <OverviewSection />,
    users: <UsersSection />,
    "bot-users": <BotUsersSection />,
    "bot-diagnostics": <BotDiagnosticsSection />,
    payments: <PaymentsSection title="All Payments" />,
    coinbase: <PaymentsSection title="Coinbase Commerce Payments" />,
    plans: <PlansSection />,
    "promo-codes": <AdminPromoSection />,
    "audit-logs": <AuditSection />,
    security: <SecuritySection />,
    diagnostics: <DiagnosticsSection />,
    "system-audit": <SystemAuditSection />,
    settings: <SettingsSection />,
    backup: <BackupSection />,
    bootstrap: <BootstrapSection />,
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-dark">
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <Header />
      <div className="flex flex-1 relative z-10">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? "w-56" : "w-14"} transition-all duration-300 shrink-0 border-r border-border/30 bg-sidebar/80 backdrop-blur-xl flex flex-col`}>
          <div className="flex items-center justify-between p-3 border-b border-border/30">
            {sidebarOpen && (
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-xs font-bold text-primary">Admin Panel</span>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-colors ml-auto">
              <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
          <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
            {sidebarItems.map((item) => (
              <button key={item.id} onClick={() => setSection(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${section === item.id ? "bg-primary/20 text-primary border border-primary/30" : "text-muted-foreground hover:bg-muted/30 hover:text-foreground"}`}>
                <item.icon className="w-4 h-4 shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              {sidebarItems.find((s) => s.id === section) && (() => {
                const item = sidebarItems.find((s) => s.id === section)!;
                return (
                  <>
                    <item.icon className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-bold text-foreground">{item.label}</h2>
                  </>
                );
              })()}
            </div>
            {sectionContent[section]}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default Admin;
